import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { enhancedRecommendationService } from '../../services/enhancedRecommendationService';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Filter,
  Plus,
  Trophy,
  Edit3,
  Eye,
  Share2,
  CheckCircle,
  Clock,
  Calendar,
  TriangleAlert,
  Bot,
  Settings,
  MoreHorizontal,
  Lightbulb,
  Download,
  Users,
  InfoIcon,
  X,
  History,
  Mail
} from 'lucide-react';
import RecommenderCard from './RecommenderCard';
import ShareButton from '../Sharing/ShareButton';
import SimpleTeacherModal from './SimpleTeacherModal';
import EnhancedSmartRecRequestFlow from './EnhancedSmartRecRequestFlow';
import RecommendationEmailHistory from './RecommendationEmailHistory';
import TeacherBragSheetFramework from './TeacherBragSheetFramework';
import ThankYouEmailComposer from './ThankYouEmailComposer';

const EnhancedRecommendationsHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useFamilyContext();

  const [recommendations, setRecommendations] = useState([]);
  const [bragSheetStats, setBragSheetStats] = useState({
    masterComplete: true,
    teacherVersions: { complete: 3, total: 7 },
    sharingActive: true,
    lastUpdated: '2 days ago'
  });
  const [loading, setLoading] = useState(true);
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [newRecommendation, setNewRecommendation] = useState(null);
  const [viewRequestModal, setViewRequestModal] = useState(null);
  const [showEnhancedSmartRecRequestFlow, setShowEnhancedSmartRecRequestFlow] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showEmailHistory, setShowEmailHistory] = useState(null);
  const [showPacketModal, setShowPacketModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [packetRecommendation, setPacketRecommendation] = useState(null);
  const [thankYouRecommendation, setThankYouRecommendation] = useState(null);

  // Handle view parameter redirects
  useEffect(() => {
    const viewParam = searchParams.get('view');

    if (viewParam === 'brag-sheet') {
      navigate('/college/brag-sheet');
      return;
    } else if (viewParam === 'follow-ups') {
      navigate('/college/recommendations/follow-up-note');
      return;
    } else if (viewParam === 'request-generator') {
      // Open the request generator modal
      setShowEnhancedSmartRecRequestFlow(true);
    } else if (viewParam === 'packet') {
      // Show packet modal - will need to select a teacher
      setShowPacketModal(true);
    } else if (viewParam === 'thank-you') {
      // Show thank you modal - will need to select a teacher
      setShowThankYouModal(true);
    }
  }, [searchParams, navigate]);

  // Load recommendations using enhanced service
  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      const enhancedData = await enhancedRecommendationService.getEnhancedRecommendations(currentUser.id, true);

      // Format the data for display
      const formattedData = enhancedData.map(rec => ({
        ...rec,
        avatar: rec.recommender_name.split(' ').map(n => n[0]).join('').toUpperCase(),
        requested_days_ago: rec.request_date
          ? Math.floor((new Date() - new Date(rec.request_date)) / (1000 * 60 * 60 * 24))
          : null,
        has_brag_sheet: rec.brag_sheet_integration?.exists || false,
        ai_enhanced: !!rec.ai_assessment,
        timeline_status: rec.timeline_analysis?.status,
        brag_sheet_status: getBragSheetStatus(rec),
        brag_sheet_completion_score: rec.brag_sheet_completion_score || 0,
        brag_sheet_workflow_state: rec.brag_sheet_workflow_state || 'not_started',
        workflow_status: getWorkflowStatus(rec)
      }));

      setRecommendations(formattedData);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, [currentUser.id]);

  useEffect(() => {
    if (currentUser?.id) {
      loadRecommendations();
    }
  }, [currentUser?.id, loadRecommendations]);

  const getBragSheetStatus = (rec) => {
    // Check workflow state first for more accurate status
    const workflowState = rec.brag_sheet_workflow_state || 'not_started';
    const completionScore = rec.brag_sheet_completion_score || 0;

    if (workflowState === 'note_generated') return 'note_complete';
    if (workflowState === 'ready' || completionScore >= 70) return 'complete';
    if (workflowState === 'in_progress' && completionScore > 0) return 'in_progress';
    if (rec.request_accepted) return 'ready_to_create';
    return 'waiting_for_acceptance';
  };

  const getWorkflowStatus = (rec) => {
    if (rec.request_declined) return 'request_declined';
    if (rec.request_accepted) return 'request_accepted';
    if (rec.request_sent) return 'request_sent';
    if (rec.request_email_content && !rec.request_sent) return 'request_created';
    if (rec.is_quick_entry) return 'setup_needed';
    // Default status for properly set up teachers is ready to create request
    return 'ready_to_request';
  };

  const getBragSheetBadge = (status, recommendation) => {
    const score = recommendation?.brag_sheet_completion_score || 0;

    switch (status) {
      case 'note_complete':
        return <span className="bg-teal-600 text-xs px-3 py-1 rounded-full text-white">Note Ready</span>;
      case 'complete':
        return <span className="bg-emerald-600 text-xs px-3 py-1 rounded-full text-white">Brag Sheet Complete</span>;
      case 'in_progress':
        return <span className="bg-gold-500 text-xs px-3 py-1 rounded-full text-white">In Progress ({score}%)</span>;
      case 'ready_to_create':
        return <span className="bg-azure-500 text-xs px-3 py-1 rounded-full text-white">Ready for Brag Sheet</span>;
      case 'waiting_for_acceptance':
        return <span className="bg-stone-400 text-xs px-3 py-1 rounded-full text-white">Awaiting Acceptance</span>;
      default:
        return null;
    }
  };

  const getWorkflowBadge = (status) => {
    switch (status) {
      case 'sent':
        return <span className="bg-azure-100 text-azure-700 text-xs px-3 py-1 rounded-full">Request Sent</span>;
      case 'accepted':
        return <span className="bg-emerald-100 text-emerald-700 text-xs px-3 py-1 rounded-full">Accepted ✓</span>;
      case 'declined':
        return <span className="bg-coral-100 text-coral-700 text-xs px-3 py-1 rounded-full">Declined</span>;
      case 'setup_needed':
        return <span className="bg-gold-100 text-gold-700 text-xs px-3 py-1 rounded-full">Setup Needed</span>;
      case 'ready_to_request':
        return <span className="bg-violet-100 text-violet-700 text-xs px-3 py-1 rounded-full">Ready to Request</span>;
      default:
        return <span className="bg-slate-100 text-slate-700 text-xs px-3 py-1 rounded-full">Not Started</span>;
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'complete':
        return 'from-emerald-500 to-teal-500';
      case 'needs_creation':
        return 'from-gold-500 to-orange-500';
      case 'setup_needed':
        return 'from-coral-500 to-magenta-500';
      default:
        return 'from-slate-400 to-slate-500';
    }
  };

  const handleCreateBragSheet = (recommendation) => {
    // Only allow brag sheet creation if request was accepted
    if (!recommendation.request_accepted) {
      toast.error('Please wait for the teacher to accept your request before creating a brag sheet');
      return;
    }

    // Navigate to standard brag sheet page with teacher pre-selected
    navigate('/college/brag-sheet', {
      state: {
        selectedTeacherId: recommendation.id,
        teacherInfo: {
          id: recommendation.id,
          name: recommendation.recommender_name,
          subject: recommendation.subject,
          email: recommendation.recommender_email
        },
        showProgressBar: true,
        fromRecommendationsHub: true
      }
    });
  };

  const handleSendRequest = (recommendation) => {
    // Launch the Smart Request Flow for this teacher
    navigate('/college/recommendations', {
      state: {
        launchSmartRequest: true,
        recommendationId: recommendation.id,
        teacherInfo: recommendation
      }
    });
  };

  const handleCreateRequest = (recommendation) => {
    // Show smart request launcher
    setSelectedRecommendation(recommendation);
    setShowEnhancedSmartRecRequestFlow(true);
  };

  const handleEditMasterBragSheet = () => {
    navigate('/college/brag-sheet');
  };

  const handlePreviewExport = () => {
    // Navigate to brag sheet in preview mode
    navigate('/college/brag-sheet', {
      state: {
        previewMode: true,
        showExport: true
      }
    });
  };

  const handleAddTeacher = () => {
    // Create a new recommendation object for the modal
    const newRec = {
      id: `temp-${Date.now()}`,
      user_id: currentUser.id,
      recommender_name: '',
      recommender_type: 'teacher',
      is_quick_entry: true
    };
    setNewRecommendation(newRec);
    setShowAddTeacherModal(true);
  };

  const handleTeacherAdded = async () => {
    setShowAddTeacherModal(false);
    setNewRecommendation(null);
    // Reload recommendations to show the newly added teacher
    await loadRecommendations();
    toast.success('Teacher added successfully!');
  };

  const handleViewRequest = (recommendation) => {
    // Show email history instead of the old request modal
    setShowEmailHistory(recommendation);
  };

  const handleEditRequest = async (recommendation) => {
    // Open the setup modal to edit the details
    setNewRecommendation(recommendation);
    setShowAddTeacherModal(true);
  };

  const handleEditDeadline = (recommendation) => {
    // Open teacher setup modal to edit deadline
    setNewRecommendation(recommendation);
    setShowAddTeacherModal(true);
  };

  const handleMarkAsSent = async (recommendation) => {
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          request_sent: true,
          request_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', recommendation.id);

      if (error) throw error;

      toast.success('Marked as sent!');
      await loadRecommendations();
    } catch (error) {
      console.error('Error marking as sent:', error);
      toast.error('Failed to update status');
    }
  };

  const handleMarkAccepted = async (recommendation) => {
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          request_accepted: true,
          request_declined: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', recommendation.id);

      if (error) throw error;

      toast.success('Great! Teacher accepted the request.');
      await loadRecommendations();
    } catch (error) {
      console.error('Error marking as accepted:', error);
      toast.error('Failed to update status');
    }
  };

  const handleMarkDeclined = async (recommendation) => {
    try {
      const { error } = await supabase
        .from('college_recommendation_letters')
        .update({
          request_declined: true,
          request_accepted: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', recommendation.id);

      if (error) throw error;

      toast.info('Marked as declined. You can try a different approach.');
      await loadRecommendations();
    } catch (error) {
      console.error('Error marking as declined:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCreateNote = (recommendation) => {
    // Navigate to FollowUpEmailComposer to create the follow-up note
    navigate('/college/recommendations/follow-up-note', {
      state: {
        recommendation: recommendation,
        recommendationId: recommendation.id,
        fromHub: true
      }
    });
  };

  const handleViewNote = (recommendation) => {
    // Navigate to view the completed note
    navigate('/college/recommendations/follow-up-note', {
      state: {
        recommendation: recommendation,
        recommendationId: recommendation.id,
        viewMode: true,
        fromHub: true
      }
    });
  };

  const progressPercentage = (bragSheetStats.teacherVersions.complete / bragSheetStats.teacherVersions.total) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-azure-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Recommendations</h1>
              <p className="text-slate-600 text-xs sm:text-sm truncate">
                <span className="hidden sm:inline">{currentUser?.name} • </span>
                {recommendations.length} teachers • {recommendations.filter(r => r.has_brag_sheet).length} complete
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button className="px-3 sm:px-4 py-2 bg-white border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 text-sm flex-1 sm:flex-initial justify-center">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>
            <button
              onClick={handleAddTeacher}
              className="px-3 sm:px-4 py-2 bg-azure-600 text-white rounded-xl hover:bg-azure-700 transition-colors flex items-center gap-2 text-sm flex-1 sm:flex-initial justify-center"
            >
              <Plus className="w-4 h-4" />
              <span>Add Teacher</span>
            </button>
          </div>
        </div>

        {/* Brag Sheet Hero Section */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-5"></div>
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Brag Sheet Central</h2>
                    <p className="text-slate-300">Make it easy for them to make you look good</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
                  <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Master Brag Sheet</h3>
                      <span className="bg-teal-500 text-xs px-2 py-1 rounded-full text-white">Complete</span>
                    </div>
                    <p className="text-slate-300 text-sm mb-3">Your comprehensive academic profile</p>
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Clock className="w-3 h-3" />
                      Last updated {bragSheetStats.lastUpdated}
                    </div>
                  </div>

                  <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Teacher Versions</h3>
                      <span className="bg-gold-400 text-[10px] w-[70px] text-center px-2 py-1 rounded-full text-slate-900">
                        {bragSheetStats.teacherVersions.complete} of {bragSheetStats.teacherVersions.total}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm mb-3">Customized for each recommender</p>
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Users className="w-3 h-3" />
                      {bragSheetStats.teacherVersions.total - bragSheetStats.teacherVersions.complete} teachers need versions
                    </div>
                  </div>

                  <div className="bg-white bg-opacity-10 rounded-xl p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Sharing Status</h3>
                      <span className="bg-teal-500 text-xs px-2 py-1 rounded-full text-white">Active</span>
                    </div>
                    <p className="text-slate-300 text-sm mb-3">Accessible to family & counselors</p>
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Share2 className="w-3 h-3" />
                      3 people have access
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={handleEditMasterBragSheet}
                    className="bg-teal-50 px-4 sm:px-6 py-2 sm:py-3 rounded-xl border border-2 border-teal-500 text-slate-900 font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Master Brag Sheet
                  </button>
                  <button
                    onClick={handlePreviewExport}
                    className="bg-white bg-opacity-20 px-4 sm:px-6 py-2 sm:py-3 rounded-xl text-white font-semibold hover:bg-opacity-30 transition-colors flex items-center gap-2 backdrop-blur-sm"
                  >
                    <Eye className="w-4 h-4" />
                    Preview & Export
                  </button>
                  <ShareButton
                    itemType="brag_sheet"
                    itemId={currentUser.id}
                    itemTitle="Master Brag Sheet"
                    currentStatus="complete"
                    className="bg-white bg-opacity-20 px-4 py-3 rounded-xl text-white hover:bg-opacity-30 transition-colors backdrop-blur-sm"
                  >
                    <Share2 className="w-4 h-4" />
                  </ShareButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Recommendations List */}
          <div className="lg:col-span-3 space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">Your Teachers</h3>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <InfoIcon className="w-4 h-4" />
                Click any teacher to create their custom brag sheet
              </div>
            </div>

            {/* Enhanced Recommendation Cards */}
            {recommendations.map((recommendation) => (
              <div key={recommendation.id} className="bg-gradient-to-r from-white to-slate-50 rounded-2xl shadow-sm border border-slate-900 overflow-hidden hover:shadow-md transition-all duration-200 hover:-translate-y-1">
                <div className={`h-1 bg-gradient-to-r ${getProgressBarColor(recommendation.brag_sheet_status)}`}></div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 sm:w-12 h-10 sm:h-12 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                        {recommendation.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">{recommendation.recommender_name}</h3>
                          {recommendation.workflow_status === 'setup_needed' && (
                            <button
                              onClick={() => {
                                setNewRecommendation(recommendation);
                                setShowAddTeacherModal(true);
                              }}
                              className="p-1 text-gold-600 hover:bg-gold-50 rounded transition-colors"
                              title="Complete setup"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-slate-600 text-sm">{recommendation.subject} • {recommendation.relationship_length || 'New'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getWorkflowBadge(recommendation.workflow_status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
                    {/* Email History Section */}
                    <div className={`rounded-lg p-3 cursor-pointer hover:bg-slate-100 transition-colors ${
                      ['request_created', 'request_sent', 'request_accepted', 'request_declined'].includes(recommendation.workflow_status)
                        ? 'bg-slate-50' : 'bg-stone-50 border border-stone-200'
                    }`}
                    onClick={() => ['request_created', 'request_sent', 'request_accepted', 'request_declined'].includes(recommendation.workflow_status) && handleViewRequest(recommendation)}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-600 uppercase font-medium">Email History</span>
                        {['request_created', 'request_sent', 'request_accepted', 'request_declined'].includes(recommendation.workflow_status) ? (
                          <History className="w-4 h-4 text-azure-500" />
                        ) : (
                          <Mail className="w-4 h-4 text-stone-400" />
                        )}
                      </div>
                      <p className="font-semibold text-slate-900">
                        {recommendation.workflow_status === 'request_sent' ? `Sent ${recommendation.requested_days_ago || 0} days ago` :
                         recommendation.workflow_status === 'request_accepted' ? 'Accepted' :
                         recommendation.workflow_status === 'request_declined' ? 'Declined' :
                         recommendation.workflow_status === 'request_created' ? 'Draft Ready' :
                         'No emails yet'}
                      </p>
                      <p className="text-xs text-slate-600">
                        {['request_created', 'request_sent', 'request_accepted', 'request_declined'].includes(recommendation.workflow_status)
                          ? 'Click to view timeline' : 'Create request first'}
                      </p>
                    </div>

                    {/* Brag Sheet Section */}
                    <div className={`rounded-lg p-3 ${
                      recommendation.brag_sheet_status === 'complete' ? 'bg-emerald-50' :
                      recommendation.brag_sheet_status === 'ready_to_create' ? 'bg-azure-50 border border-azure-200' :
                      'bg-stone-50 border border-stone-200'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-600 uppercase font-medium">Brag Sheet</span>
                        {recommendation.brag_sheet_status === 'complete' ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : recommendation.brag_sheet_status === 'ready_to_create' ? (
                          <Trophy className="w-4 h-4 text-azure-600" />
                        ) : (
                          <TriangleAlert className="w-4 h-4 text-stone-500" />
                        )}
                      </div>
                      <p className="font-semibold text-slate-900">
                        {recommendation.brag_sheet_status === 'complete' ? 'Complete' :
                         recommendation.brag_sheet_status === 'ready_to_create' ? 'Ready to Create' :
                         'Setup Needed'}
                      </p>
                      <p className="text-xs text-slate-600">
                        {recommendation.brag_sheet_status === 'complete' ? 'Custom packet created' :
                         recommendation.brag_sheet_status === 'ready_to_create' ? 'Teacher accepted request' :
                         'Wait for teacher acceptance'}
                      </p>
                    </div>

                    {/* Deadline Section */}
                    <div className="bg-slate-50 rounded-lg p-3 cursor-pointer hover:bg-slate-100 transition-colors"
                         onClick={() => handleEditDeadline(recommendation)}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-600 uppercase font-medium">Deadline</span>
                        <Calendar className="w-4 h-4 text-azure-500" />
                      </div>
                      <p className="font-semibold text-slate-900">
                        {recommendation.due_date ? format(new Date(recommendation.due_date), 'MMM d') : 'TBD'}
                      </p>
                      <p className="text-xs text-slate-600">
                        {recommendation.due_date ?
                          Math.ceil((new Date(recommendation.due_date) - new Date()) / (1000 * 60 * 60 * 24)) > 0 ?
                            `${Math.ceil((new Date(recommendation.due_date) - new Date()) / (1000 * 60 * 60 * 24))} days left` :
                            'Overdue'
                          : 'Click to set deadline'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Primary Action Button */}
                      {recommendation.workflow_status === 'ready_to_request' ? (
                        <button
                          onClick={() => handleCreateRequest(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <Bot className="w-4 h-4" />
                          Create Recommendation Request
                        </button>
                      ) : recommendation.workflow_status === 'request_created' ? (
                        <button
                          onClick={() => handleMarkAsSent(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Mark as Sent
                        </button>
                      ) : recommendation.workflow_status === 'request_sent' ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMarkDeclined(recommendation)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-2"
                          >
                            <X className="w-4 h-4" />
                            Declined
                          </button>
                          <button
                            onClick={() => handleMarkAccepted(recommendation)}
                            className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm flex items-center gap-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Accepted
                          </button>
                        </div>
                      ) : recommendation.workflow_status === 'request_accepted' && recommendation.brag_sheet_status === 'note_complete' ? (
                        <button
                          onClick={() => handleViewNote(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Note
                        </button>
                      ) : recommendation.workflow_status === 'request_accepted' && recommendation.brag_sheet_status === 'complete' ? (
                        <button
                          onClick={() => handleCreateNote(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/30"
                        >
                          <Plus className="w-4 h-4" />
                          Create Note
                        </button>
                      ) : recommendation.workflow_status === 'request_accepted' && recommendation.brag_sheet_status === 'in_progress' ? (
                        <button
                          onClick={() => handleCreateBragSheet(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <Edit3 className="w-4 h-4" />
                          Continue Packet ({recommendation.brag_sheet_completion_score}%)
                        </button>
                      ) : recommendation.workflow_status === 'request_accepted' && recommendation.brag_sheet_status === 'ready_to_create' ? (
                        <button
                          onClick={() => handleCreateBragSheet(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors text-sm flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Create Packet
                        </button>
                      ) : recommendation.workflow_status === 'request_declined' ? (
                        <button
                          onClick={() => handleCreateRequest(recommendation)}
                          className="px-3 sm:px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm flex items-center gap-2"
                        >
                          <Bot className="w-4 h-4" />
                          Try Different Approach
                        </button>
                      ) : null}
                    </div>
                    <button className="text-slate-400 hover:text-slate-600 transition-colors">
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Progress Overview */}
            <div className="bg-gradient-to-r from-white to-slate-50 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4">Progress Overview</h3>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Setup Complete</span>
                    <span className="text-sm text-slate-600">{recommendations.filter(r => r.workflow_status !== 'setup_needed').length} of {recommendations.length}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-gold-400 to-orange-500 h-2 rounded-full" style={{ width: `${recommendations.length > 0 ? (recommendations.filter(r => r.workflow_status !== 'setup_needed').length / recommendations.length) * 100 : 0}%` }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Requests Sent</span>
                    <span className="text-sm text-slate-600">{recommendations.filter(r => r.request_sent || r.workflow_status === 'sent').length} of {recommendations.length}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-azure-400 to-blue-600 h-2 rounded-full" style={{ width: `${recommendations.length > 0 ? (recommendations.filter(r => r.request_sent || r.workflow_status === 'sent').length / recommendations.length) * 100 : 0}%` }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Accepted</span>
                    <span className="text-sm text-slate-600">{recommendations.filter(r => r.request_accepted || r.workflow_status === 'accepted').length} of {recommendations.length}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-emerald-400 to-green-600 h-2 rounded-full" style={{ width: `${recommendations.length > 0 ? (recommendations.filter(r => r.request_accepted || r.workflow_status === 'accepted').length / recommendations.length) * 100 : 0}%` }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Smart Suggestions */}
            <div className="bg-gradient-to-r from-white to-slate-50 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4">Smart Suggestions</h3>

              <div className="space-y-4">
                <div className="bg-red-50 border border-red-500 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Lightbulb className="w-4 h-4 text-red-600" />
                    <span className="font-semibold text-coral-800">Priority Action</span>
                  </div>
                  <p className="text-red-700 text-sm mb-3">Create brag sheet for upcoming deadlines!</p>
                  <button className="w-full px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm">
                    Create Now
                  </button>
                </div>

                <div className="bg-indigo-50 border border-indigo-500 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Bot className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold text-indigo-800">AI Insight</span>
                  </div>
                  <p className="text-azure-700 text-sm mb-3">Your master brag sheet could use 2 more achievements to strengthen it.</p>
                  <button
                    onClick={handleEditMasterBragSheet}
                    className="w-full px-3 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
                  >
                    Enhance
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-white to-slate-50 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-4">Quick Actions</h3>

              <div className="space-y-3">
                <button
                  onClick={handleAddTeacher}
                  className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add New Teacher
                </button>
                <button className="w-full px-4 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" />
                  Export All Brag Sheets
                </button>
                <button className="w-full px-4 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm flex items-center justify-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share with Counselor
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Teacher Modal */}
      {showAddTeacherModal && newRecommendation && (
        <SimpleTeacherModal
          recommendation={newRecommendation}
          onComplete={handleTeacherAdded}
          onClose={() => {
            setShowAddTeacherModal(false);
            setNewRecommendation(null);
          }}
        />
      )}

      {/* Smart Request Launcher */}
      {showEnhancedSmartRecRequestFlow && selectedRecommendation && (
        <EnhancedSmartRecRequestFlow
          recommenderId={selectedRecommendation.id}
          recommenderData={selectedRecommendation}
          onBack={() => {
            setShowEnhancedSmartRecRequestFlow(false);
            setSelectedRecommendation(null);
          }}
          onComplete={() => {
            setShowEnhancedSmartRecRequestFlow(false);
            setSelectedRecommendation(null);
            loadRecommendations(); // Refresh the list
          }}
        />
      )}

      {/* Email History Modal */}
      {showEmailHistory && (
        <RecommendationEmailHistory
          recommendation={showEmailHistory}
          onClose={() => setShowEmailHistory(null)}
        />
      )}

      {/* Teacher Packet/Brag Sheet Modal */}
      {showPacketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {packetRecommendation ? (
              <TeacherBragSheetFramework
                recommendationId={packetRecommendation.id}
                recommendation={packetRecommendation}
                onClose={() => {
                  setShowPacketModal(false);
                  setPacketRecommendation(null);
                }}
              />
            ) : (
              <div className="p-4 sm:p-6">
                <h2 className="text-xl font-semibold mb-4">Select a Teacher</h2>
                <p className="text-stone-600 mb-4">Choose a recommender to create their customized packet:</p>
                <div className="space-y-2">
                  {recommendations.filter(r => r.status === 'accepted').map(rec => (
                    <button
                      key={rec.id}
                      onClick={() => setPacketRecommendation(rec)}
                      className="w-full text-left p-4 border-2 border-stone-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all"
                    >
                      <div className="font-medium">{rec.recommender_name}</div>
                      <div className="text-sm text-stone-600">{rec.subject || rec.relationship}</div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowPacketModal(false)}
                  className="mt-4 px-3 sm:px-4 py-2 text-stone-600 hover:text-stone-900"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Thank You Letter Modal */}
      {showThankYouModal && (
        thankYouRecommendation ? (
          <ThankYouEmailComposer
            recommendation={thankYouRecommendation}
            isOpen={showThankYouModal}
            onClose={() => {
              setShowThankYouModal(false);
              setThankYouRecommendation(null);
            }}
          />
        ) : (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6">
              <h2 className="text-xl font-semibold mb-4">Select a Teacher</h2>
              <p className="text-stone-600 mb-4">Choose a recommender to thank:</p>
              <div className="space-y-2">
                {recommendations.filter(r => r.status === 'accepted').map(rec => (
                  <button
                    key={rec.id}
                    onClick={() => setThankYouRecommendation(rec)}
                    className="w-full text-left p-4 border-2 border-stone-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all"
                  >
                    <div className="font-medium">{rec.recommender_name}</div>
                    <div className="text-sm text-stone-600">{rec.subject || rec.relationship}</div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowThankYouModal(false)}
                className="mt-4 px-3 sm:px-4 py-2 text-stone-600 hover:text-stone-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default EnhancedRecommendationsHub;